// packages/web/src/components/LessonMaterialUploader.tsx — misma lógica (subida paralela por SAS, preview IA, edición del resumen).
// Cambios: 3 estados visibles (subir → analizar → revisar), zona de arrastre real, filas de archivo con
// estado en texto + icono, resumen editable en 2 columnas con labels asociados.
import { useCallback, useId, useState } from 'react';
import { CheckCircle2, FileText, Loader2, Sparkles, Trash2, Upload, AlertCircle } from 'lucide-react';
import {
  LESSON_MATERIAL_MAX_BYTES,
  LESSON_MATERIAL_MAX_FILES,
  LESSON_MATERIAL_MIMES,
  type LessonMaterialAttach,
  type LessonMaterialFileRef,
  type LessonMaterialMime,
  type LessonMaterialSummary,
} from '@marzano/contracts';
import { api, ApiError } from '../api';
import { Alert } from './ui/Alert';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { INPUT_CLASS } from './ui/Field';

interface Props {
  oaCodigo: string | null;
  oaDescripcion: string | null;
  nivelAcademico: string | null;
  asignatura: string | null;
  onReady: (attach: LessonMaterialAttach | null) => void;
}
interface LocalFile { ref: LessonMaterialFileRef; status: 'uploading' | 'uploaded' | 'failed'; error?: string }
type Phase = 'idle' | 'uploading' | 'summarizing' | 'ready' | 'error';

const MIME_LABELS: Record<LessonMaterialMime, string> = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'text/plain': 'Texto',
  'image/jpeg': 'Imagen',
  'image/png': 'Imagen',
  'image/webp': 'Imagen',
};
const MAX_MB = LESSON_MATERIAL_MAX_BYTES / 1024 / 1024;
const fmtSize = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`);

function detectMime(file: File): LessonMaterialMime | null {
  const t = file.type as LessonMaterialMime;
  if ((LESSON_MATERIAL_MIMES as readonly string[]).includes(t)) return t;
  if (file.name.toLowerCase().endsWith('.txt')) return 'text/plain';
  return null;
}

export function LessonMaterialUploader({ oaCodigo, oaDescripcion, nivelAcademico, asignatura, onReady }: Props): JSX.Element {
  const [files, setFiles] = useState<LocalFile[]>([]);
  const [phase, setPhase] = useState<Phase>('idle');
  const [summary, setSummary] = useState<LessonMaterialSummary | null>(null);
  const [attachRef, setAttachRef] = useState<LessonMaterialAttach | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const contextReady = oaCodigo !== null && oaDescripcion !== null && nivelAcademico !== null && asignatura !== null;

  const handleFiles = useCallback(async (picked: File[]) => {
    setError(null);
    if (files.length + picked.length > LESSON_MATERIAL_MAX_FILES) { setError(`Máximo ${LESSON_MATERIAL_MAX_FILES} archivos por material.`); return; }
    const newFiles: LocalFile[] = picked.map((f) => {
      const mime = detectMime(f);
      if (!mime) return { ref: { blob_ref: '', mime: 'text/plain', filename: f.name, size_bytes: f.size }, status: 'failed' as const, error: 'Formato no permitido. Sube PDF, Word, TXT o imagen (JPG, PNG o WebP).' };
      if (f.size > LESSON_MATERIAL_MAX_BYTES) return { ref: { blob_ref: '', mime, filename: f.name, size_bytes: f.size }, status: 'failed' as const, error: `Supera ${MAX_MB} MB.` };
      return { ref: { blob_ref: '', mime, filename: f.name, size_bytes: f.size }, status: 'uploading' as const };
    });
    setFiles((prev) => [...prev, ...newFiles]);
    setPhase('uploading');
    setSummary(null); setAttachRef(null); onReady(null);

    await Promise.all(picked.map(async (f, idx) => {
      const baseIdx = files.length + idx;
      const local = newFiles[idx]!;
      if (local.status === 'failed') return;
      try {
        const mime = local.ref.mime;
        const sas = await api.getLessonMaterialUploadUrl({ mime, size_bytes: f.size, filename: f.name });
        await api.putLessonMaterialToBlob(sas.upload_url, mime, f);
        setFiles((prev) => { const copy = [...prev]; copy[baseIdx] = { ref: { blob_ref: sas.blob_ref, mime, filename: f.name, size_bytes: f.size }, status: 'uploaded' }; return copy; });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setFiles((prev) => { const copy = [...prev]; copy[baseIdx] = { ...copy[baseIdx]!, status: 'failed', error: msg }; return copy; });
      }
    }));
    setPhase('idle');
  }, [files.length, onReady]);

  const handleRemove = useCallback((idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setSummary(null); setAttachRef(null); onReady(null);
  }, [onReady]);

  const handleSummarize = useCallback(async () => {
    if (!contextReady) return;
    const uploaded = files.filter((f) => f.status === 'uploaded').map((f) => f.ref);
    if (uploaded.length === 0) { setError('Sube al menos un archivo antes de generar el resumen.'); return; }
    setPhase('summarizing'); setError(null);
    try {
      const resp = await api.previewLessonMaterial({ files: uploaded, oa_codigo: oaCodigo!, oa_descripcion: oaDescripcion!, nivel_academico: nivelAcademico!, asignatura: asignatura! });
      setSummary(resp.summary); setAttachRef(resp.attach_ref); onReady(resp.attach_ref); setPhase('ready');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
      setPhase('error');
    }
  }, [asignatura, contextReady, files, nivelAcademico, oaCodigo, oaDescripcion, onReady]);

  const uploadedCount = files.filter((f) => f.status === 'uploaded').length;
  const busy = phase === 'uploading' || phase === 'summarizing';
  const disabled = busy || !contextReady;
  const stage = summary ? 3 : uploadedCount > 0 ? 2 : 1;

  return (
    <Card title="Material de la clase" description="Opcional. Si lo subes, las preguntas se basan en lo que viste con el curso y no en el OA genérico."
      actions={summary ? <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-800"><CheckCircle2 aria-hidden className="w-4 h-4" />Listo para usar</span> : undefined}>
      <ol className="flex flex-wrap gap-x-5 gap-y-1 text-[13px]" aria-label="Pasos del material">
        {['Sube archivos', 'Analiza con IA', 'Revisa el resumen'].map((t, i) => (
          <li key={t} aria-current={stage === i + 1 ? 'step' : undefined} className={`flex items-center gap-1.5 ${stage === i + 1 ? 'font-semibold text-marzano-700' : stage > i + 1 ? 'text-emerald-800' : 'text-slate-600'}`}>
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${stage > i + 1 ? 'bg-emerald-700 text-white' : stage === i + 1 ? 'bg-marzano-600 text-white' : 'bg-slate-200 text-slate-700'}`}>{stage > i + 1 ? '✓' : i + 1}</span>{t}
          </li>
        ))}
      </ol>

      {!contextReady && <Alert kind="info">Primero elige nivel, asignatura, hito y OA en el paso 1. El resumen usa ese contexto.</Alert>}

      <label
        onDragOver={(e) => { if (!disabled) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (disabled) return; const picked = Array.from(e.dataTransfer.files ?? []); if (picked.length) void handleFiles(picked); }}
        className={
          'block rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-amber-500 ' +
          (disabled ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
            : dragOver ? 'border-marzano-600 bg-marzano-50 cursor-copy'
            : 'border-slate-300 hover:border-marzano-500 hover:bg-slate-50 cursor-pointer')
        }>
        <input type="file" multiple className="sr-only" accept={LESSON_MATERIAL_MIMES.join(',') + ',.txt'} disabled={disabled}
          onChange={(e) => { const picked = Array.from(e.target.files ?? []); if (picked.length > 0) void handleFiles(picked); e.target.value = ''; }} />
        <Upload aria-hidden className="w-6 h-6 mx-auto text-marzano-600 mb-1.5" />
        <span className="block text-sm font-semibold text-slate-900">{phase === 'uploading' ? 'Subiendo…' : 'Arrastra archivos o haz clic para elegir'}</span>
        <span className="block text-[13px] text-slate-600 mt-0.5">PDF, Word, TXT o imagen · hasta {LESSON_MATERIAL_MAX_FILES} archivos de {MAX_MB} MB · {files.length}/{LESSON_MATERIAL_MAX_FILES}</span>
      </label>

      {files.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {files.map((f, i) => (
            <li key={i} className="flex items-center gap-3 px-3.5 py-2.5 text-sm">
              <FileText aria-hidden className="w-4 h-4 text-slate-500 shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block truncate text-slate-900">{f.ref.filename}</span>
                <span className="block text-xs text-slate-600">{MIME_LABELS[f.ref.mime] ?? f.ref.mime} · {fmtSize(f.ref.size_bytes)}</span>
                {f.status === 'failed' && f.error && <span className="block text-xs font-medium text-red-700">{f.error}</span>}
              </span>
              {f.status === 'uploading' && <span className="inline-flex items-center gap-1 text-xs text-slate-600 shrink-0"><Loader2 aria-hidden className="w-3.5 h-3.5 animate-spin" />Subiendo</span>}
              {f.status === 'uploaded' && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 shrink-0"><CheckCircle2 aria-hidden className="w-3.5 h-3.5" />Subido</span>}
              {f.status === 'failed' && <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 shrink-0"><AlertCircle aria-hidden className="w-3.5 h-3.5" />Falló</span>}
              <button type="button" onClick={() => handleRemove(i)} disabled={busy} aria-label={`Quitar ${f.ref.filename}`}
                className="p-1.5 rounded-md text-slate-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-40 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500">
                <Trash2 aria-hidden className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <Alert kind="error">{error}</Alert>}

      {uploadedCount > 0 && !summary && (
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" className="!bg-marzano-600 !text-white !border-marzano-600 hover:!bg-marzano-700"
            leftIcon={<Sparkles aria-hidden className="w-4 h-4" />} onClick={() => void handleSummarize()} loading={phase === 'summarizing'} disabled={!contextReady || busy}>
            {phase === 'summarizing' ? 'Analizando material…' : `Analizar ${uploadedCount} ${uploadedCount === 1 ? 'archivo' : 'archivos'}`}
          </Button>
          <span className="text-[13px] text-slate-600">La IA extrae temas, términos y ejemplos para orientar las preguntas.</span>
        </div>
      )}

      {summary && attachRef && (
        <SummaryPreview summary={summary} onChange={(updated) => {
          setSummary(updated);
          const nextAttach: LessonMaterialAttach = { ...attachRef, summary: updated };
          setAttachRef(nextAttach);
          onReady(nextAttach);
        }} />
      )}
    </Card>
  );
}

function SummaryPreview({ summary, onChange }: { summary: LessonMaterialSummary; onChange: (updated: LessonMaterialSummary) => void }): JSX.Element {
  const updateList = (key: 'temas_priorizados' | 'terminos_clave' | 'ejemplos_dados' | 'ejercicios_tipo', text: string): void => {
    const items = text.split('\n').map((l) => l.replace(/^[-•]\s*/, '').trim()).filter((l) => l.length > 0);
    onChange({ ...summary, [key]: items });
  };
  return (
    <section aria-labelledby="sum-t" className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 space-y-4">
      <div>
        <h3 id="sum-t" className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Sparkles aria-hidden className="w-4 h-4 text-marzano-600" />Resumen del material</h3>
        <p className="text-[13px] text-slate-600 mt-0.5">Revisa lo que extrajo la IA y corrige lo necesario. Un ítem por línea.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SummaryField label="Temas priorizados" items={summary.temas_priorizados} onChange={(t) => updateList('temas_priorizados', t)} />
        <SummaryField label="Términos clave" items={summary.terminos_clave} onChange={(t) => updateList('terminos_clave', t)} />
        <SummaryField label="Ejemplos vistos en clase" items={summary.ejemplos_dados} onChange={(t) => updateList('ejemplos_dados', t)} />
        <SummaryField label="Tipos de ejercicios" items={summary.ejercicios_tipo} onChange={(t) => updateList('ejercicios_tipo', t)} />
      </div>
      {summary.notas && <Alert kind="info"><strong>Nota de la IA:</strong> {summary.notas}</Alert>}
    </section>
  );
}

function SummaryField({ label, items, onChange }: { label: string; items: readonly string[]; onChange: (text: string) => void }): JSX.Element {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="flex items-baseline justify-between text-sm font-medium text-slate-800">{label}<span className="text-xs font-normal text-slate-600 tabular-nums">{items.length}</span></label>
      <textarea id={id} className={INPUT_CLASS + ' font-mono text-[13px] leading-relaxed resize-y'} rows={Math.max(3, items.length)}
        value={items.map((i) => `- ${i}`).join('\n')} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
